import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import PhygitalIcon from "@/components/PhygitalIcon";
import { toast } from "sonner";
import { useUiSurface, slotText } from "@/hooks/useUiSurface";
import { useAuth } from "@/App";
import {
  ArrowRight,
  Check,
  Eye,
  Share,
  Calendar,
  Users,
  FolderOpen,
  DollarSign,
  MessageSquare,
  BarChart3,
  Settings,
  Bell,
  Image,
  Sparkles,
  Link2,
  LayoutDashboard,
  ImageIcon,
  MessageCircle,
  Briefcase,
  AlertCircle,
  Lock,
  ExternalLink,
  PartyPopper,
  Brain,
  Heart,
  Palette,
  Scissors,
  Leaf,
  ShoppingBag,
  BarChart
} from "lucide-react";
import { getCategoryPreset } from "../lib/categoryConfig";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Demo nav items for dropdown - all 11 pilot categories
const LANDING_DEMO_ITEMS = [
  { group: "Beauty", items: [
    { catKey: "hair", path: "/hair", desc: "Hair stylists" },
    { catKey: "makeup", path: "/mua", desc: "Makeup artists" },
    { catKey: "nails", path: "/nails", desc: "Nail technicians" },
    { catKey: "lashes", path: "/lash", desc: "Lash artists" },
  ]},
  { group: "Experiences", items: [
    { catKey: "events", path: "/events", desc: "Event hosts" },
    { catKey: "culinary", path: "/culinary", desc: "Chefs & caterers" },
    { catKey: "wellness", path: "/wellness", desc: "Wellness providers" },
    { catKey: "instructors", path: "/instructor", desc: "Instructors & coaches" },
  ]},
  { group: "Lifestyle", items: [
    { catKey: "travel", path: "/travel", desc: "Travel concierge" },
    { catKey: "dispensary", path: "/dispensaries", desc: "Dispensaries" },
    { catKey: "style", path: "/retail", desc: "Retail & shopping" },
  ]},
];

// Airtable early access link
const EARLY_ACCESS_URL = "https://airtable.com/appNbAMRVh9q5LfdL/pagPizNm6i77SbZoO/form";

// Access codes for different pilots
const ACCESS_CODES = {
  "HTHF2026": { redirect: "/e/hthf-lafw-2026", pilot: "events" },
  "MUAPILOT": { redirect: "/mua", pilot: "mua" },
  "HAIRPILOT": { redirect: "/hair", pilot: "hair" },
  "PHYGITAL888": { redirect: "/home", pilot: "full" },
  "DEMO": { redirect: "/home", pilot: "demo" }
};

// Animated word cycle for headline
const ROTATING_WORDS = [
  { word: "hair", tagline: "Your hair preferences, perfectly communicated" },
  { word: "makeup", tagline: "Your makeup preferences, perfectly communicated" },
  { word: "nail", tagline: "Your nail preferences, perfectly communicated" },
  { word: "lash", tagline: "Your lash preferences, perfectly communicated" },
  { word: "event", tagline: "Your event preferences, perfectly communicated" }
];

const LandingPage = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  // Iter 9.47.m - Authenticated users skip the public marketing page and go
  // straight to their /me dashboard. The marketing copy here is stealth-mode
  // and still applies to non-authenticated visitors.
  useEffect(() => {
    if (isAuthenticated) navigate("/me", { replace: true });
  }, [isAuthenticated, navigate]);
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [showAccessModal, setShowAccessModal] = useState(false);
  const [accessCode, setAccessCode] = useState("");
  const [showDemoDropdown, setShowDemoDropdown] = useState(false);
  // Iter 9.47.l - landing hero/CTA copy from Airtable.
  const { slotMap: lp } = useUiSurface('surface_landing');

  useEffect(() => {
    document.title = "phygital - Your preferences, perfectly communicated";
  }, []);

  useEffect(() => {    
    const interval = setInterval(() => {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentWordIndex((prev) => (prev + 1) % ROTATING_WORDS.length);
        setIsAnimating(false);
      }, 300);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  const currentItem = ROTATING_WORDS[currentWordIndex];

  const handleAccessCode = () => {
    const code = accessCode.toUpperCase().trim();
    const access = ACCESS_CODES[code];
    
    if (access) {
      localStorage.setItem("pilot_access", access.pilot);
      toast.success("Access granted!");
      window.location.href = access.redirect;
    } else {
      toast.error("Invalid access code");
    }
  };

  const handleGetStarted = () => {
    setShowAccessModal(true);
  };

  const stylistTools = [
    { icon: Users, label: "Clients", color: "bg-blue-100 text-blue-600" },
    { icon: Calendar, label: "Bookings", color: "bg-green-100 text-green-600" },
    { icon: Image, label: "Portfolio", color: "bg-purple-100 text-purple-600" },
    { icon: DollarSign, label: "Payments", color: "bg-amber-100 text-amber-600" },
    { icon: MessageSquare, label: "Messages", color: "bg-pink-100 text-pink-600" },
    { icon: BarChart3, label: "Analytics", color: "bg-indigo-100 text-indigo-600" },
    { icon: Bell, label: "Reminders", color: "bg-red-100 text-red-600" },
    { icon: Settings, label: "Settings", color: "bg-gray-100 text-gray-700" },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Access Code Modal */}
      {showAccessModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <Card className="w-full max-w-sm">
            <CardContent className="p-6">
              <div className="text-center mb-6">
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-gray-100 flex items-center justify-center">
                  <Lock className="w-6 h-6 text-gray-700" />
                </div>
                <h3 className="font-semibold text-lg">Enter Access Code</h3>
                <p className="text-sm text-gray-600">For pilot testers and partners</p>
              </div>
              <div className="space-y-4">
                <Input
                  placeholder="Enter code"
                  value={accessCode}
                  onChange={(e) => setAccessCode(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleAccessCode()}
                  className="text-center uppercase tracking-widest"
                  data-testid="access-code-input"
                />
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => setShowAccessModal(false)}
                  >
                    Cancel
                  </Button>
                  <Button 
                    className="flex-1"
                    onClick={handleAccessCode}
                    data-testid="access-code-submit"
                  >
                    Enter
                  </Button>
                </div>
                <div className="text-center pt-2 border-t border-gray-100">
                  <p className="text-xs text-gray-500 mb-2">Don&apos;t have a code?</p>
                  <a 
                    href={EARLY_ACCESS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-purple-600 hover:text-purple-700 font-medium inline-flex items-center gap-1"
                  >
                    Get Early Access
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-white to-gray-50" />
        
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-16">
          {/* Nav */}
          <nav className="flex items-center justify-between mb-12">
            <div className="flex items-center gap-2 sm:gap-3">
              <PhygitalIcon className="w-9 h-9 sm:w-10 sm:h-10" variant="light" />
              <span className="font-semibold text-xl sm:text-2xl">phygital</span>
            </div>
            <div className="flex items-center gap-2 sm:gap-4">
              {/* Demo Dropdown */}
              <div className="relative">
                <button 
                  onClick={() => setShowDemoDropdown(!showDemoDropdown)}
                  onBlur={() => setTimeout(() => setShowDemoDropdown(false), 150)}
                  className="flex items-center gap-1 text-gray-700 hover:text-gray-900 font-medium text-sm px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors"
                  data-testid="demo-toggle-btn"
                >
                  <Eye className="w-4 h-4" />
                  <span>Demos</span>
                  <svg className={`w-4 h-4 transition-transform ${showDemoDropdown ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {showDemoDropdown && (
                  <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden z-50">
                    <div className="p-2">
                      {LANDING_DEMO_ITEMS.map((group, gi) => (
                        <div key={gi}>
                          {gi > 0 && <div className="border-t border-gray-100 my-2" />}
                          <p className="text-xs text-gray-500 uppercase tracking-wide px-3 py-2">{group.group}</p>
                          {group.items.map(item => {
                            const preset = getCategoryPreset(item.catKey);
                            const Icon = preset?.icon || Sparkles;
                            return (
                              <Link key={item.catKey} to={item.path} className="flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 rounded-lg transition-colors" data-testid={`demo-${item.catKey}-link`}>
                                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: preset?.bgColor }}>
                                  <Icon className="w-4 h-4" style={{ color: preset?.iconColor }} />
                                </div>
                                <div>
                                  <p className="font-medium text-sm text-gray-900">{preset?.name} Demo</p>
                                  <p className="text-xs text-gray-600">{item.desc}</p>
                                </div>
                              </Link>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <Button 
                onClick={handleGetStarted}
                className="rounded-full bg-black hover:bg-gray-800 text-sm px-4 sm:px-6"
                data-testid="get-started-btn"
              >
                {slotText(lp, 'nav_get_started', 'Get Started')}
              </Button>
            </div>
          </nav>

          {/* Hero Content */}
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-[26px] sm:text-4xl md:text-5xl font-display font-bold text-gray-900 mb-4 sm:mb-6 leading-tight" data-testid="landing-hero-title">
              {slotText(lp, 'hero_static_prefix', 'Your')}{" "}
              <span 
                className={`inline-block transition-all duration-300 bg-gradient-to-r from-pink-500 to-purple-500 bg-clip-text text-transparent ${
                  isAnimating ? 'opacity-0 translate-y-2' : 'opacity-100 translate-y-0'
                }`}
              >
                {currentItem.word}
              </span>
              {" "}{slotText(lp, 'hero_static_suffix', 'preferences,')}
              <br />
              <span className="block mt-1">{slotText(lp, 'hero_perfectly', 'perfectly communicated')}</span>
            </h1>

            <p className="text-[15px] sm:text-base md:text-lg text-gray-700 max-w-2xl mx-auto mb-6 sm:mb-8 px-2 sm:px-4" data-testid="landing-hero-subtitle">
              <span className="sm:hidden whitespace-nowrap">{slotText(lp, 'hero_subtitle_short', 'Store your preferences once. Share with anyone.')}</span>
              <span className="hidden sm:inline">{slotText(lp, 'hero_subtitle_long', 'Store your preferences once. Share with anyone, anywhere.')}</span>
            </p>

            {/* CTAs - Both open access code modal */}
            <div className="flex flex-row gap-3 justify-center max-w-md mx-auto mb-6">
              <Button 
                onClick={handleGetStarted}
                className="flex-1 h-12 sm:h-14 text-sm sm:text-base bg-black hover:bg-gray-800 text-white rounded-full"
                data-testid="store-share-btn"
              >
                <Share className="w-4 h-4 mr-2" />
                {slotText(lp, 'cta_store_share', 'Store and Share')}
              </Button>
              <Button 
                onClick={handleGetStarted}
                variant="outline"
                className="flex-1 h-12 sm:h-14 text-sm sm:text-base rounded-full border-2 border-gray-300 hover:border-gray-400"
                data-testid="for-providers-btn"
              >
                <Briefcase className="w-4 h-4 mr-2" />
                {slotText(lp, 'cta_for_providers', 'For Providers')}
              </Button>
            </div>

            {/* Trust badges */}
            <div className="flex items-center justify-center gap-4 text-xs sm:text-sm text-gray-600">
              <span className="flex items-center gap-1" data-testid="trust-free">
                <Check className="w-3 h-3 sm:w-4 sm:h-4 text-green-500" />
                {slotText(lp, 'trust_free', 'Free forever')}
              </span>
              <span className="flex items-center gap-1" data-testid="trust-no-app">
                <Check className="w-3 h-3 sm:w-4 sm:h-4 text-green-500" />
                {slotText(lp, 'trust_no_app', 'No app needed')}
              </span>
              <span className="flex items-center gap-1 whitespace-nowrap" data-testid="trust-works-anywhere">
                <Check className="w-3 h-3 sm:w-4 sm:h-4 text-green-500" />
                {slotText(lp, 'trust_works_anywhere', 'Works anywhere')}
              </span>
            </div>
          </div>

          {/* Stacked Preference Cards Preview */}
          <div className="mt-10 sm:mt-14 max-w-sm mx-auto relative h-64">
            {/* Event Card - Back */}
            <div className="absolute left-1/2 top-0 -translate-x-1/2 w-64 rotate-6 transition-all hover:rotate-0 hover:scale-105 z-10">
              <div className="bg-gradient-to-br from-amber-500 to-orange-500 rounded-2xl p-4 shadow-lg">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
                    <PartyPopper className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-white font-semibold text-sm">Event Preferences</span>
                </div>
                <div className="space-y-1.5 text-white/90 text-xs">
                  <div className="bg-white/20 rounded-lg px-2 py-1">Dietary: Vegan, nut-free</div>
                  <div className="bg-white/20 rounded-lg px-2 py-1">Accessibility: Wheelchair</div>
                </div>
              </div>
            </div>
            
            {/* Nails Card - Middle */}
            <div className="absolute left-1/2 top-8 -translate-x-1/2 w-64 -rotate-3 transition-all hover:rotate-0 hover:scale-105 z-20">
              <div className="bg-gradient-to-br from-pink-500 to-purple-500 rounded-2xl p-4 shadow-lg">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
                    <Sparkles className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-white font-semibold text-sm">Nail Preferences</span>
                </div>
                <div className="space-y-1.5 text-white/90 text-xs">
                  <div className="bg-white/20 rounded-lg px-2 py-1">Shape: Almond, medium</div>
                  <div className="bg-white/20 rounded-lg px-2 py-1">Style: Gel, French tips</div>
                </div>
              </div>
            </div>
            
            {/* Hair Card - Front */}
            <div className="absolute left-1/2 top-16 -translate-x-1/2 w-64 rotate-2 transition-all hover:rotate-0 hover:scale-105 z-30">
              <div className="bg-gradient-to-br from-blue-500 to-teal-500 rounded-2xl p-4 shadow-lg">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
                    <Scissors className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-white font-semibold text-sm">Hair Preferences</span>
                </div>
                <div className="space-y-1.5 text-white/90 text-xs">
                  <div className="bg-white/20 rounded-lg px-2 py-1">Knotless braids, bob length, edges</div>
                  <div className="bg-white/20 rounded-lg px-2 py-1 flex items-center gap-1">
                    <MessageCircle className="w-3 h-3" />
                    Tired today, not chatty
                  </div>
                  <div className="bg-white/20 rounded-lg px-2 py-1 flex items-center gap-1">
                    <ImageIcon className="w-3 h-3" />
                    Inspo pic attached
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-12 sm:py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-display font-bold text-gray-900 mb-8 sm:mb-10 text-center">
            How it works
          </h2>

          <div className="grid grid-cols-3 gap-3 sm:gap-6 max-w-3xl mx-auto">
            {[
              { num: "1", title: "Set up", desc: "Your preferences" },
              { num: "2", title: "Share", desc: "Send a link or QR" },
              { num: "3", title: "Done", desc: "Get what you want" }
            ].map((step) => (
              <div key={step.num} className="text-center">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-black flex items-center justify-center mx-auto mb-2 sm:mb-3">
                  <span className="text-base sm:text-lg font-bold text-white">{step.num}</span>
                </div>
                <h3 className="font-semibold text-sm sm:text-base mb-1">{step.title}</h3>
                <p className="text-gray-700 text-xs sm:text-sm">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* For Clients Section */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-10 items-center">
            <div className="text-center lg:text-left lg:pr-4">
              <span className="inline-block px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs sm:text-sm font-medium mb-3 sm:mb-4">
                For You
              </span>
              <h2 className="text-xl sm:text-2xl md:text-3xl font-display font-bold text-gray-900 mb-3 sm:mb-4">
                Your preferences, <span className="bg-gradient-to-r from-cyan-500 via-teal-500 to-emerald-500 bg-clip-text text-transparent">everywhere</span>
              </h2>
              <p className="text-gray-700 text-sm sm:text-base mb-6">
                Stop explaining yourself at every appointment. Store your preferences once and share with any stylist.
              </p>
              
              <div className="grid grid-cols-2 gap-3 sm:gap-4 max-w-md mx-auto lg:mx-0">
                {[
                  { icon: Share, title: "Share with anyone", desc: "Link or QR code" },
                  { icon: FolderOpen, title: "All profiles in one place", desc: "Hair, nails, makeup, lash" },
                  { icon: Eye, title: "Preview before sharing", desc: "See what they see" },
                  { icon: Heart, title: "Accessibility included", desc: "Invisible disabilities supported" }
                ].map((feature, i) => (
                  <div key={i} className="flex flex-col items-center text-center p-3 bg-gray-50 rounded-xl">
                    <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center shadow-sm mb-2">
                      <feature.icon className="w-4 h-4 text-gray-700" />
                    </div>
                    <h3 className="font-medium text-sm">{feature.title}</h3>
                    <p className="text-xs text-gray-600">{feature.desc}</p>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Invisible Disabilities Visual */}
            <div className="flex justify-center order-last lg:order-last lg:pl-4">
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-5 shadow-lg border border-purple-100 max-w-[300px]">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                    <Heart className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-sm text-gray-900">Invisible Support</p>
                    <p className="text-xs text-gray-600">Share what others can&apos;t see</p>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <div className="bg-white rounded-lg p-3 border border-purple-100">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center">
                        <MessageCircle className="w-3 h-3 text-blue-600" />
                      </div>
                      <span className="text-xs font-medium text-gray-700">Vocal Tic</span>
                    </div>
                    <p className="text-xs text-gray-600 pl-8">May make sudden noises involuntarily</p>
                  </div>
                  
                  <div className="bg-white rounded-lg p-3 border border-purple-100">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center">
                        <Brain className="w-3 h-3 text-amber-600" />
                      </div>
                      <span className="text-xs font-medium text-gray-700">Autism</span>
                    </div>
                    <p className="text-xs text-gray-600 pl-8">Prefers routine, may avoid eye contact</p>
                  </div>
                  
                  <div className="bg-white rounded-lg p-3 border border-purple-100">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-6 h-6 rounded-full bg-pink-100 flex items-center justify-center">
                        <AlertCircle className="w-3 h-3 text-pink-600" />
                      </div>
                      <span className="text-xs font-medium text-gray-700">Sensory Sensitivity</span>
                    </div>
                    <p className="text-xs text-gray-600 pl-8">Fragrance-free, gentle touch</p>
                  </div>
                </div>
                
                <p className="text-xs text-purple-600 text-center mt-3 font-medium">
                  Your stylist sees this before you arrive
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* For Stylists Section */}
      <section className="py-12 sm:py-16 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-10 items-center">
            <div className="text-center lg:text-left order-first lg:pr-4">
              <span className="inline-block px-3 py-1 bg-pink-100 text-pink-700 rounded-full text-xs sm:text-sm font-medium mb-3 sm:mb-4">
                For Stylists
              </span>
              <h2 className="text-xl sm:text-2xl md:text-3xl font-display font-bold text-gray-900 mb-3 sm:mb-4">
                <span>Organize all your tools </span>
                <br className="hidden lg:block" />
                <span className="lg:pl-0 bg-gradient-to-r from-pink-500 via-purple-500 to-violet-500 bg-clip-text text-transparent">in one place</span>
              </h2>
              <p className="text-gray-700 text-sm sm:text-base mb-4">
                Link Acuity, Square, and StyleSeat. Manage your entire client journey without switching between apps.
              </p>
              
              <div className="grid grid-cols-1 gap-3 max-w-md mx-auto lg:mx-0 mb-4">
                {[
                  { icon: Users, title: "Client directory", desc: "Preferences, history, notes" },
                  { icon: Image, title: "Portfolio showcase", desc: "Import from Instagram or TikTok" },
                  { icon: DollarSign, title: "Collect deposits", desc: "Venmo, CashApp, Zelle, Apple Pay" },
                  { icon: Link2, title: "Connect your tools", desc: "Acuity, Square, StyleSeat" }
                ].map((feature, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 bg-white rounded-xl border border-gray-100 lg:bg-transparent lg:border-0 lg:p-0">
                    <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center flex-shrink-0 shadow-sm border border-gray-100">
                      <feature.icon className="w-4 h-4 text-gray-700" />
                    </div>
                    <div className="text-left">
                      <h3 className="font-medium text-sm">{feature.title}</h3>
                      <p className="text-xs text-gray-600">{feature.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              
              <Button 
                onClick={handleGetStarted}
                className="mt-4 rounded-full bg-black hover:bg-gray-800 text-sm"
              >
                Get Started Free
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
            
            {/* App Grid */}
            <div className="flex justify-center order-last lg:order-last lg:pl-4">
              <div className="bg-white rounded-2xl p-4 sm:p-5 shadow-lg border border-gray-200">
                <p className="text-center text-gray-600 text-xs sm:text-sm mb-3 sm:mb-4">Your tools, one place</p>
                <div className="grid grid-cols-4 gap-2 sm:gap-3">
                  {stylistTools.map((tool) => (
                    <div key={tool.label} className="flex flex-col items-center gap-1">
                      <div className={`w-10 h-10 sm:w-12 sm:h-12 ${tool.color} rounded-xl flex items-center justify-center`}>
                        <tool.icon className="w-5 h-5 sm:w-6 sm:h-6" />
                      </div>
                      <span className="text-xs text-gray-700">{tool.label}</span>
                    </div>
                  ))}
                </div>
                <div className="border-t border-gray-100 mt-4 pt-3">
                  <p className="text-xs text-gray-500 text-center mb-2">Connects with</p>
                  <div className="flex items-center justify-center gap-2">
                    <div className="px-2 py-1 bg-gray-100 rounded text-xs font-medium text-gray-700">Acuity</div>
                    <div className="px-2 py-1 bg-gray-100 rounded text-xs font-medium text-gray-700">Square</div>
                    <div className="px-2 py-1 bg-gray-100 rounded text-xs font-medium text-gray-700">StyleSeat</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Beauty Services Section */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-10 items-center">
            <div className="text-center lg:text-left lg:pr-4">
              <span className="inline-block px-3 py-1 bg-pink-100 text-pink-700 rounded-full text-xs sm:text-sm font-medium mb-3 sm:mb-4">
                Beauty Services
              </span>
              <h2 className="text-xl sm:text-2xl md:text-3xl font-display font-bold text-gray-900 mb-3 sm:mb-4">
                Your <span className="text-pink-500">beauty</span> preferences,
                <br />perfectly communicated
              </h2>
              <p className="text-gray-700 text-sm sm:text-base mb-6 max-w-sm">
                Hair, nails, makeup, lashes...the best way to exchange style inspo with your glam squad.
              </p>
              
              <div className="grid grid-cols-2 gap-3 sm:gap-4 max-w-md mx-auto lg:mx-0">
                {[
                  { icon: Scissors, title: "Hair and Styling", desc: "Texture, length, products" },
                  { icon: Sparkles, title: "Nails", desc: "Shape, length, art preferences" },
                  { icon: Palette, title: "Makeup", desc: "Coverage, finish, allergies" },
                  { icon: Eye, title: "Lashes", desc: "Style, curl, fullness" }
                ].map((feature, i) => (
                  <div key={i} className="flex flex-col items-center text-center p-3 bg-pink-50/50 rounded-xl">
                    <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center shadow-sm mb-2">
                      <feature.icon className="w-4 h-4 text-pink-600" />
                    </div>
                    <h3 className="font-medium text-sm">{feature.title}</h3>
                    <p className="text-xs text-gray-600">{feature.desc}</p>
                  </div>
                ))}
              </div>
              
              <Button 
                onClick={handleGetStarted}
                className="mt-6 rounded-full bg-pink-600 hover:bg-pink-700 text-sm"
              >
                Create Your Beauty Profile
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
            
            <div className="flex justify-center order-last lg:order-last lg:pl-4">
              <div className="bg-gradient-to-br from-pink-100 to-purple-100 rounded-2xl p-6 shadow-lg">
                <div className="bg-white rounded-xl p-4 shadow-sm">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-400 to-purple-500 flex items-center justify-center text-white font-medium">
                      R
                    </div>
                    <div>
                      <p className="font-semibold text-sm">Rozie&apos;s Beauty Profile</p>
                      <p className="text-xs text-gray-600">Shared with 3 stylists</p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="bg-pink-50 rounded-lg p-3">
                      <p className="text-xs text-pink-700 font-medium">Hair Preferences</p>
                      <p className="text-sm text-gray-700">Silk press, natural products, tender-headed</p>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-3">
                      <p className="text-xs text-purple-700 font-medium">Nail Preferences</p>
                      <p className="text-sm text-gray-700">Almond shape, medium length, gel preferred</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Events Section */}
      <section className="py-12 sm:py-16 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-10 items-center">
            <div className="flex justify-center order-last lg:order-first lg:pr-4">
              <div className="bg-gradient-to-br from-amber-100 to-orange-100 rounded-2xl p-6 shadow-lg">
                <div className="bg-white rounded-xl p-4 shadow-sm">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 rounded-xl bg-black flex items-center justify-center">
                        <PartyPopper className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="font-semibold text-sm">Event Dashboard</p>
                        <p className="text-xs text-gray-600">Your Event OS</p>
                      </div>
                    </div>
                    <span className="text-xs font-medium px-2 py-1 bg-green-100 text-green-700 rounded-full">Live</span>
                  </div>
                  
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center justify-between bg-gray-50 rounded-lg p-2">
                      <span className="text-xs text-gray-700">RSVPs</span>
                      <span className="text-sm font-semibold">147</span>
                    </div>
                    <div className="flex items-center justify-between bg-gray-50 rounded-lg p-2">
                      <span className="text-xs text-gray-700">Dietary needs</span>
                      <span className="text-sm font-semibold text-amber-600">23</span>
                    </div>
                    <div className="flex items-center justify-between bg-gray-50 rounded-lg p-2">
                      <span className="text-xs text-gray-700">Accessibility</span>
                      <span className="text-sm font-semibold text-blue-600">8</span>
                    </div>
                  </div>
                  
                  <div className="border-t border-gray-100 pt-3">
                    <p className="text-xs text-gray-500 mb-2">Works with</p>
                    <div className="flex items-center gap-2">
                      <div className="px-2 py-1 bg-gray-100 rounded text-xs font-medium text-gray-700">Partiful</div>
                      <div className="px-2 py-1 bg-gray-100 rounded text-xs font-medium text-gray-700">Posh.vip</div>
                      <div className="px-2 py-1 bg-gray-100 rounded text-xs font-medium text-gray-700">Eventbrite</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="text-center lg:text-left order-first lg:order-last lg:pl-4">
              <div className="flex items-center gap-2 justify-center lg:justify-start mb-3 sm:mb-4">
                <span className="inline-block px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-xs sm:text-sm font-medium">
                  Events
                </span>
                <span className="inline-block px-3 py-1 bg-green-500 text-white rounded-full text-xs sm:text-sm font-bold">
                  DEMO LIVE
                </span>
              </div>
              <h2 className="text-xl sm:text-2xl md:text-3xl font-display font-bold text-gray-900 mb-3 sm:mb-4">
                Your <span className="text-orange-500">event</span> preferences,
                <br />perfectly communicated
              </h2>
              <p className="text-gray-700 text-sm sm:text-base mb-4">
                Connect your booking link and store your entire event operating system in one place.
              </p>
              
              <div className="grid grid-cols-2 gap-3 sm:gap-4 max-w-md mx-auto lg:mx-0 mb-6">
                {[
                  { icon: Link2, title: "Connect Bookings", desc: "Partiful, Posh, Eventbrite" },
                  { icon: LayoutDashboard, title: "Admin Dashboard", desc: "RSVPs and analytics" },
                  { icon: Users, title: "Team Access", desc: "Share with your crew" },
                  { icon: Heart, title: "Guest Preferences", desc: "Dietary, accessibility, vibe" }
                ].map((feature, i) => (
                  <div key={i} className="flex flex-col items-center text-center p-3 bg-orange-50/50 rounded-xl">
                    <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center shadow-sm mb-2">
                      <feature.icon className="w-4 h-4 text-orange-600" />
                    </div>
                    <h3 className="font-medium text-sm">{feature.title}</h3>
                    <p className="text-xs text-gray-600">{feature.desc}</p>
                  </div>
                ))}
              </div>
              
              <Link to="/events">
                <Button className="rounded-full bg-orange-500 hover:bg-orange-600 text-white text-sm px-6">
                  See Events Demo
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Coming Soon Section */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center max-w-2xl mx-auto">
            <span className="inline-block px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs sm:text-sm font-medium mb-3 sm:mb-4">
              Coming Soon
            </span>
            <h2 className="text-xl sm:text-2xl md:text-3xl font-display font-bold text-gray-900 mb-3 sm:mb-4">
              Your <span className="text-gray-500">[experience]</span> preferences,
              <br />perfectly communicated
            </h2>
            <p className="text-gray-700 text-sm sm:text-base mb-8">
              We&apos;re expanding to more verticals.<br />
              Share your preferences across all your IRL experiences.
            </p>
            
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-xl mx-auto">
              {[
                { icon: Leaf, name: "Dispensaries", color: "bg-green-100 text-green-600" },
                { icon: Heart, name: "Wellness", color: "bg-teal-100 text-teal-600" },
                { icon: ShoppingBag, name: "Retail", color: "bg-blue-100 text-blue-600" },
                { icon: Calendar, name: "Conferences", color: "bg-purple-100 text-purple-600" }
              ].map((item) => (
                <div key={item.name} className="flex flex-col items-center p-4 bg-gray-50 rounded-xl">
                  <div className={`w-12 h-12 rounded-xl ${item.color} flex items-center justify-center mb-2`}>
                    <item.icon className="w-6 h-6" />
                  </div>
                  <span className="text-sm font-medium text-gray-700">{item.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 text-center">
          <PhygitalIcon className="w-12 h-12 sm:w-14 sm:h-14 mx-auto mb-4 sm:mb-6" />
          <h2 className="text-xl sm:text-2xl md:text-3xl font-display font-bold text-gray-900 mb-3 sm:mb-4">
            Ready to simplify your experience?
          </h2>
          <p className="text-sm sm:text-base text-gray-700 mb-2">
            Store your preferences once and share them anywhere.
          </p>
          <p className="text-xs sm:text-sm text-gray-500 mb-6">
            Takes less than 2 minutes.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button 
              onClick={handleGetStarted}
              className="h-12 px-6 text-sm bg-black hover:bg-gray-800 text-white rounded-full"
            >
              Get Started Free
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <a 
              href={EARLY_ACCESS_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button 
                variant="outline"
                className="h-12 px-6 text-sm rounded-full border-2 w-full sm:w-auto"
              >
                Get Early Access
                <ExternalLink className="w-4 h-4 ml-2" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 sm:py-10 bg-gray-50 border-t border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col gap-6">
            {/* Top Row - Logo and Tagline */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <PhygitalIcon className="w-7 h-7" />
                <span className="font-display text-lg font-bold">phygital</span>
              </div>
              <p className="text-gray-600 text-xs sm:text-sm text-center">
                © 2026 Phygital. Your preferences, perfectly communicated.
              </p>
            </div>
            
            {/* Demo Links Row */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 py-4 border-t border-b border-gray-200">
              <span className="text-xs text-gray-500 uppercase tracking-wide">Demos:</span>
              <div className="flex items-center gap-4 sm:gap-6">
                <Link to="/events" className="flex items-center gap-1.5 text-sm text-gray-700 hover:text-gray-900 transition-colors">
                  <PartyPopper className="w-4 h-4 text-orange-500" />
                  Events
                </Link>
                <Link to="/mua" className="flex items-center gap-1.5 text-sm text-gray-700 hover:text-gray-900 transition-colors">
                  <Palette className="w-4 h-4 text-pink-500" />
                  MUA
                </Link>
                <Link to="/hair" className="flex items-center gap-1.5 text-sm text-gray-700 hover:text-gray-900 transition-colors">
                  <Scissors className="w-4 h-4 text-purple-500" />
                  Hair
                </Link>
                <Link to="/nail" className="flex items-center gap-1.5 text-sm text-gray-700 hover:text-gray-900 transition-colors">
                  <Sparkles className="w-4 h-4 text-pink-400" />
                  Nail
                </Link>
              </div>
            </div>
            
            {/* Bottom Row - Legal Links */}
            <div className="flex items-center justify-center gap-4 sm:gap-6 text-xs sm:text-sm text-gray-600">
              <Link to="/privacy" className="hover:text-gray-900">Privacy</Link>
              <Link to="/terms" className="hover:text-gray-900">Terms</Link>
              <a href="mailto:hello@phygital.systems" className="hover:text-gray-900">Contact</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
